import Highcharts, { Options, SeriesSunburstOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useState } from 'react';
import {
    connectedColor,
    disconnectedColor,
    shippedColor,
    sleepColor,
    deployedColor,
    returnedColor,
    retiredColor,
    returnedArrivedColor,
    undeployedColor,
} from 'store/services/device-status';
import { SupplierStat } from 'types';
import DarkTheme from 'highcharts/themes/high-contrast-dark';
import LightTheme from 'highcharts/themes/high-contrast-light';
import { useUi } from 'hooks/useUi';
import sunburst from 'highcharts/modules/sunburst';
sunburst(Highcharts); // Need to initialize some highcharts imported modules apparently https://stackoverflow.com/questions/63855636/highcharts-with-react-error-missingmodule-with-chart-type-of-timeline.

export interface StatusSunburstChartProps {
    highcharts?: typeof Highcharts;
    supplierStats: SupplierStat;
    isInternalUser: boolean;
}

export const StatusSunburstChart = ({
    highcharts,
    supplierStats,
    isInternalUser,
}: StatusSunburstChartProps) => {
    const [options, setOptions] = useState<Options>({});
    const { ui } = useUi();

    // Set graph theme based on user's color theme.
    useEffect(() => {
        if (ui.theme === 'dark') DarkTheme(Highcharts);
        else LightTheme(Highcharts);
    }, [ui.theme]);

    useEffect(() => {
        const defaultOptions: Options = {
            title: { text: 'Current Status' },
            colors: ['transparent'],
            credits: { enabled: false },
            chart: {
                height: '100%',
                width: 400,
            },
            plotOptions: {
                sunburst: {},
            },
            series: [],
        };

        const calculateUndeployedValue = (): number => {
            if (isInternalUser) {
                return (
                    (supplierStats.deviceCount ?? 0) -
                    (supplierStats.deployCount ?? 0)
                );
            } else {
                return (
                    // Non-internal users should not see any data about retired, returned, or returned arrived monitors.
                    // Remove these from the Undeployed total for these users so that there isn't a visibly missing chunk where retired/returned/return arrived monitors would be in the chart.
                    (supplierStats.deviceCount ?? 0) -
                    (supplierStats.deployCount ?? 0) -
                    (supplierStats?.status?.retired ?? 0) -
                    (supplierStats?.status?.returned ?? 0) -
                    (supplierStats.status?.['return-arrived'] ?? 0)
                );
            }
        };

        if (supplierStats) {
            const updatedSeries: SeriesSunburstOptions[] = [
                {
                    type: 'sunburst',
                    data: [],
                    levels: [
                        {
                            level: 1,
                            dataLabels: {
                                filter: {
                                    property: 'outerArcLength',
                                    operator: '>',
                                    value: 0,
                                },
                            },
                        },
                    ],
                    name: 'Root',
                    cursor: 'pointer',
                    dataLabels: {
                        format: '{point.name}',
                        rotationMode: 'circular',
                    },
                    allowTraversingTree: true,
                    tooltip: {
                        pointFormat: '{point.name}: {point.value}',
                    },
                },
            ];

            if (updatedSeries[0].data) {
                updatedSeries[0].data.push({
                    id: 'all',
                    parent: '',
                    name: 'All Monitors',
                });
                updatedSeries[0].data.push({
                    id: 'deployed',
                    parent: 'all',
                    name: 'Deployed',
                    color: deployedColor,
                    value: supplierStats.deployCount,
                });
                updatedSeries[0].data.push({
                    id: 'undeployed',
                    parent: 'all',
                    name: 'Undeployed',
                    color: undeployedColor,
                    value: calculateUndeployedValue(),
                });
                updatedSeries[0].data.push({
                    id: 'connected',
                    parent: 'deployed',
                    name: 'Connected',
                    value: supplierStats.connectCount,
                    color: connectedColor,
                });
                updatedSeries[0].data.push({
                    id: 'disconnected',
                    parent: 'deployed',
                    name: 'Disconnected',
                    value:
                        (supplierStats.deployCount ?? 0) -
                        (supplierStats.connectCount ?? 0),
                    color: disconnectedColor,
                });
                updatedSeries[0].data.push({
                    id: 'shipped',
                    parent: 'undeployed',
                    name: 'Shipped',
                    value: supplierStats.status?.shipped,
                    color: shippedColor,
                });
                updatedSeries[0].data.push({
                    id: 'sleep',
                    parent: 'undeployed',
                    name: 'Sleep',
                    value: supplierStats.status?.sleep,
                    color: sleepColor,
                });

                if (isInternalUser) {
                    updatedSeries[0].data.push({
                        id: 'retired',
                        parent: 'undeployed',
                        name: 'Retired',
                        value: supplierStats?.status?.retired,
                        color: retiredColor,
                    });
                    updatedSeries[0].data.push({
                        id: 'returned',
                        parent: 'undeployed',
                        name: 'Returned',
                        value: supplierStats.status?.returned,
                        color: returnedColor,
                    });
                    updatedSeries[0].data.push({
                        id: 'return-arrived',
                        parent: 'undeployed',
                        name: 'Returned Arrived',
                        value: supplierStats.status?.['return-arrived'],
                        color: returnedArrivedColor,
                    });
                }

                defaultOptions.series = updatedSeries;
            }

            setOptions(defaultOptions);
        }
    }, [isInternalUser, supplierStats]);

    return (
        <div data-testid="StatusSunburstChart">
            <HighchartsReact
                highcharts={highcharts ?? Highcharts}
                options={options}
            />
        </div>
    );
};
